/**
 * 24 - Black Box (10 §2): an aircraft is down north of the belt and nobody knows quite where. Three wrecks
 * are marked on his map, out of sight of one another; one is the aircraft, the others were stripped long
 * ago. Four seconds at the real one takes the recorder. An enemy search party is on its way to the same
 * wreck, and arrives when a careful man would.
 *
 * Rule 1.4 ("never one secret instance among lookalikes"): the engine has one `crashSite` prop and no way to
 * draw it burnt out, so the difference is made of things it CAN draw and is told on the HUD from the first
 * second - the real wreck has its cargo still strewn round it, the stripped ones stand on bare ground - and
 * only the real one offers "TAKE THE RECORDER". All three are flagged, because all three are on his map.
 */

import type { PropDef } from '../../sim/types';
import type { Point } from '../types';
import { checker } from './helpers';
import { clearing, flagTargets, keepClear, openNorth, RECALL, strew, sweepRound, wokenBy } from './helpers3b';
import { searchParty } from './helpers3bSearch';
import { carefulSeconds, fairTimer, timedRule } from './helpers3bTime';
import type { ArchetypeSpec } from './types';

const HOLD_SECONDS = 4;
/**
 * The least the wrecks stand apart. Not a number somebody liked: a soldier sees 22 m by day, so at 32 m a
 * picket on one wreck can never see a man at another. They are PLACED as far apart as the ground allows
 * (usually well over 40 m); this is only the floor, and the open ground north of the belt can be 70 x 43 m.
 */
const APART = 32;
const TAG = 'searchParty';
/** Crates within this of a wreck are its cargo. */
const CARGO_REACH = 9;
/** A picketed wreck stands at least this far from the quiet way in: a soldier sees 22 m by day. */
const PICKET_STAND_OFF = 32;
/** Clear ground a wreck wants: its 8 x 9 m footprint and a walk round it. */
const SITE_RADIUS = 7.5;

/** Two men at threat 1-3, three at 4-6, four from 7. */
export const partyFor = (threat: number): number => 2 + Math.min(2, Math.floor(threat / 3.5));
/** Stripped wrecks with a picket on them, at most: none below threat 4, one to 6, both from 7. */
export const picketsFor = (threat: number): number => (threat >= 7 ? 2 : threat >= 4 ? 1 : 0);

const cargoOf = (def: { props: PropDef[] }, site: PropDef): number =>
  def.props.filter((p) => p.kind === 'crate' && Math.abs(p.x - site.x) <= CARGO_REACH && Math.abs(p.z - site.z) <= CARGO_REACH).length;

export const blackBox: ArchetypeSpec = {
  id: 'black-box',
  name: 'BLACK BOX',
  nativeBiome: 'arctic',
  baseThreat: 4,
  lengthTiles: 160,
  clockScale: 1,
  startAlert: 0,
  garrison: 'regular',
  perimeter: 'none',
  sealed: false,
  layout: 'compound',
  size: { w: [46, 54], d: [28, 32] },
  river: false,
  patrolLoop: 'outside',
  breach: { min: 0, max: 0 },

  build(g) {
    // One wreck on the goal's own ground, the others anywhere north of the belt's wire: well apart, clear of the pickup.
    const from: Point[] = [];
    const sites: PropDef[] = [];
    const clearOf = [{ at: g.ctx.plan.pickup, radius: 16 }];
    // Nor on top of the end of the quiet way in: a wreck there would wall it off.
    if (g.ctx.quiet.end) clearOf.push({ at: g.ctx.quiet.end, radius: 14 });
    const ground = openNorth(g);
    const middle = { x: (ground.x0 + ground.x1) / 2, z: (ground.z0 + ground.z1) / 2 };
    // Three points well apart only fit if none of them sits in the middle: the first is put as far toward a
    // corner of the open ground as the goal's own rectangle lets it go, and the other two spread out from it.
    const cornerward = (p: Point): number => (p.x - middle.x) * (p.x - middle.x) + (p.z - middle.z) * (p.z - middle.z);
    for (let i = 0; i < 3; i++) {
      const at = i === 0
        ? clearing(g, { within: g.rect, radius: SITE_RADIUS, clearOf, score: cornerward })
        : clearing(g, { within: ground, radius: SITE_RADIUS, minDist: APART, from, clearOf, spread: true });
      if (!at) g.map.problems.push(`black-box: no room for three wrecks ${APART} m apart`);
      else {
        if (i === 0) from.push(at);
        sites.push(g.map.add('crashSite', at.x, at.z));
      }
    }
    const real = sites[g.rng.int(Math.max(1, sites.length))];
    let pickets = picketsFor(g.threat);
    // A picket's round must not lie across the quiet way in: from the last ravine's end to the goal's quiet corner.
    const c = g.rect;
    const quietWay: Point[] = [{ x: g.ctx.plan.roadEast ? c.x0 : c.x1, z: c.z1 }];
    if (g.ctx.quiet.end) quietWay.push(g.ctx.quiet.end);
    const farFrom = (site: Point): boolean =>
      quietWay.every((p) => (p.x - site.x) * (p.x - site.x) + (p.z - site.z) * (p.z - site.z) > PICKET_STAND_OFF * PICKET_STAND_OFF);
    for (const site of sites) {
      if (site === real) {
        if (strew(g, 'crate', 4, site, 6, 8.5) < 2) g.map.problems.push('black-box: no room for the cargo round the wreck');
      } else if (farFrom(site) && pickets-- > 0) {
        // Somebody is always poking about an old wreck: two men who walk round it, and can be timed like any patrol.
        sweepRound(g, site, 2, g.elite ? 'elite' : 'rifleman', 3.5, openNorth(g));
      }
      keepClear(g, site, 8);
    }
    if (real) {
      const kind = g.elite || g.threat >= 6 ? 'elite' : 'rifleman';
      searchParty(g, { focus: real, half: { x: 4, z: 4.5 }, standOff: 3.5, men: partyFor(g.threat), kind, tag: TAG, laps: 4 });
    }
    const ids = sites.map((s) => s.id);
    const at = real ?? { x: g.rect.x0, z: g.rect.z0 };
    return {
      objective: {
        kind: 'useObjects', label: 'RECOVER THE RECORDER', targetPropIds: real ? [real.id] : [], minFraction: 1,
        holdSeconds: HOLD_SECONDS, useLabel: 'TAKE THE RECORDER',
        rules: [
          {
            type: 'timedEvent', atSeconds: 300, effect: 'searchPartyArrives', wakes: TAG,
            orders: 'THREE WRECKS ARE MARKED. TWO WERE STRIPPED LONG AGO AND STAND ON BARE GROUND; THE RECORDER IS IN THE '
              + 'ONE WITH ITS CARGO STILL ROUND IT - AND AN ENEMY SEARCH PARTY IS MAKING FOR THAT ONE TOO',
            warning: 'ENEMY SEARCH PARTY AT THE WRECK IN',
            reason: 'THE SEARCH PARTY HAS THE RECORDER',
          },
          RECALL,
        ],
      },
      finish(def) {
        flagTargets(def, ids);
        const rule = timedRule(def, 'searchPartyArrives');
        if (rule) rule.atSeconds = carefulSeconds(def, def.player, at);
      },
    };
  },

  asserts(def, probe) {
    const { check, out } = checker('black-box');
    const o = def.objective;
    const wrecks = def.props.filter((p) => p.kind === 'crashSite');
    const real = wrecks.find((p) => p.id === o.targetPropIds[0]);
    check(o.kind === 'useObjects' && o.holdSeconds === HOLD_SECONDS, `objective must be useObjects with a ${HOLD_SECONDS} s hold`);
    check(wrecks.length === 3, `the map must show three wrecks, has ${wrecks.length}`);
    check(real !== undefined && o.targetPropIds.length === 1, 'exactly one wreck holds the recorder');
    check(wrecks.every((p) => p.target === true), 'every candidate wreck must be marked on the strip map');
    for (const a of wrecks) {
      for (const b of wrecks) {
        if (a.id >= b.id) continue;
        const d = Math.sqrt((a.x - b.x) * (a.x - b.x) + (a.z - b.z) * (a.z - b.z));
        check(d >= APART - 0.5, `wrecks ${a.id} and ${b.id} are only ${Math.round(d)} m apart`);
      }
    }
    // The tell must be there to be read: cargo round the real wreck, bare ground round the others.
    if (real) {
      check(cargoOf(def, real) >= 2, 'the real wreck must have its cargo strewn round it');
      check(probe.breach(real.id) === 0, 'the real wreck must be reachable without crossing mines or wire');
    }
    for (const w of wrecks) if (w !== real) check(cargoOf(def, w) === 0, `the stripped wreck ${w.id} must stand on bare ground`);
    const rule = timedRule(def, 'searchPartyArrives');
    check(
      rule?.orders !== undefined && /TWO WERE STRIPPED/.test(rule.orders) && rule.warning !== undefined && rule.reason !== undefined,
      'the orders must say that two of the wrecks are empty, and the search party needs its words',
    );
    const party = wokenBy(def, rule?.wakes ?? 'searchPartyArrives');
    check(party.length === partyFor(def.threat), `the search party must number ${partyFor(def.threat)} at threat ${def.threat}`);
    if (real) {
      const reaches = (s: { patrol?: Array<[number, number]> }): boolean =>
        (s.patrol ?? []).some(([x, z]) => Math.abs(x - real.x) <= 12 && Math.abs(z - real.z) <= 12);
      check(party.every(reaches), 'the search party must converge on the real wreck');
      check(fairTimer(def, rule, def.player, real), 'the search party must not be there before a careful man can be');
    }
    return out;
  },
};
